import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'
import { ALL_LANGUAGES, DEFAULT_TOP_LANGUAGE_CODES, type Language } from '../../lib/constants/languages'

interface SingleLanguageSelectorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  languages?: Language[] // Optional: custom languages list
  topLanguageCodes?: string[] // Optional: custom top languages (for ordering)
  emptyMessage?: string // Optional: custom message when no languages are found (default: "No languages found")
  popoverZIndex?: string // Optional: custom z-index for popover (default: "z-50")
}

export function SingleLanguageSelector({ 
  value, 
  onChange, 
  disabled,
  languages = ALL_LANGUAGES,
  topLanguageCodes = DEFAULT_TOP_LANGUAGE_CODES,
  emptyMessage,
  popoverZIndex = 'z-50'
}: SingleLanguageSelectorProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const defaultEmptyMessage = emptyMessage || t('common.noLanguagesFound')

  // Get selected language details
  const selectedLanguage = languages.find((lang) => lang.code === value)

  // Organize languages
  const { topLanguages, otherLanguages } = useMemo(() => {
    const top = languages.filter((lang) => topLanguageCodes.includes(lang.code))
      .sort((a, b) => {
        // Sort by order in topLanguageCodes
        const indexA = topLanguageCodes.indexOf(a.code)
        const indexB = topLanguageCodes.indexOf(b.code)
        return indexA - indexB
      })
    const other = languages.filter((lang) => !topLanguageCodes.includes(lang.code))
      .sort((a, b) => {
        // Sort alphabetically by name
        return a.name.localeCompare(b.name)
      })

    return { topLanguages: top, otherLanguages: other }
  }, [languages, topLanguageCodes])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="single-language-selector-btn w-full justify-between font-normal"
        >
          {selectedLanguage ? (
            <span className="flex items-center gap-2">
              <span>{selectedLanguage.flag}</span>
              <span>{selectedLanguage.name}</span>
              <span className="text-muted-foreground">({selectedLanguage.code})</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{t('common.selectLanguagePlaceholder')}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("single-language-selector-popover w-[400px] p-0", popoverZIndex)} align="start">
        <div className="max-h-[300px] overflow-y-auto p-1">
          {topLanguages.length > 0 && (
            <div>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {t('common.commonLanguages')}
              </div>
              {topLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    onChange(language.code)
                    setOpen(false)
                  }}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                    value === language.code && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === language.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="mr-2">{language.flag}</span>
                  <span className="flex-1 text-left">{language.name}</span>
                  <span className="text-xs text-muted-foreground">{language.code}</span>
                </button>
              ))}
            </div>
          )}
          {otherLanguages.length > 0 && (
            <div className={topLanguages.length > 0 ? 'mt-2 border-t pt-2' : ''}>
              {topLanguages.length > 0 && (
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {t('common.otherLanguages')}
                </div>
              )}
              {otherLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    onChange(language.code)
                    setOpen(false)
                  }}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                    value === language.code && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === language.code ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="mr-2">{language.flag}</span>
                  <span className="flex-1 text-left">{language.name}</span>
                  <span className="text-xs text-muted-foreground">{language.code}</span>
                </button>
              ))}
            </div>
          )}
          {topLanguages.length === 0 && otherLanguages.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {defaultEmptyMessage}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

